import {
	AuthenticateSmartcheckSessionDto,
	ModuleDefinition,
	ModuleDefinitionType,
	ProcessingStatusDto,
	QuestionnaireStep,
	SmartCheckConfigurationDto,
} from '../../types/authenticateSmartcheckSessionDto'
import config from '#config'
import { userConsentManager } from '../../userConsentManager'
import { logger } from './flowOrchestrationService'
import { HistoryWithEntries } from '../../routeOrchestrator'

// #region Future Questionnaire module types
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const canAndShouldRunPersonalQuestionnaire: FlowModule['canRunModule'] = ({
	caseConfiguration,
	questionnaireStatus,
}) => {
	switch (true) {
		case hasModule(
			caseConfiguration,
			ModuleDefinition.PersonalInformationQuestionnaire,
		): {
			return questionnaireStatus.some(
				(step) => step.type === 'sensitive' && !step.answered,
			)
		}
		case hasModule(
			caseConfiguration,
			ModuleDefinition.PersonalInformationCprOnlyQuestionnaire,
		): {
			return questionnaireStatus.some(
				(step) => step.type === 'sensitiveCprOnly' && !step.answered,
			)
		}
		case hasModule(
			caseConfiguration,
			ModuleDefinition.PersonalInformationExtendedQuestionnaire,
		): {
			return questionnaireStatus.some(
				(step) => step.type === 'sensitiveExtended' && !step.answered,
			)
		}
		default: {
			return false
		}
	}
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const canAndShouldRunAssetQuestionnaire: FlowModule['canRunModule'] = ({
	caseConfiguration,
	questionnaireStatus,
}) => {
	switch (true) {
		case hasModule(caseConfiguration, ModuleDefinition.AssetQuestionnaire): {
			return questionnaireStatus.some(
				(step) => step.type === 'car' && !step.answered,
			)
		}
		default: {
			return false
		}
	}
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const canAndShouldRunHouseholdQuestionnaire: FlowModule['canRunModule'] = ({
	caseConfiguration,
	questionnaireStatus,
}) => {
	switch (true) {
		case hasModule(
			caseConfiguration,
			ModuleDefinition.HouseholdQuestionnaire,
		): {
			return questionnaireStatus.some(
				(step) => step.type === 'household' && !step.answered,
			)
		}
		default: {
			return false
		}
	}
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const canAndShouldRunHouseholdSingleCohabitantQuestionnaire: FlowModule['canRunModule'] =
	({ caseConfiguration, questionnaireStatus }) =>
		hasModule(
			caseConfiguration,
			ModuleDefinition.HouseholdSingleCohabitantQuestionnaire,
		)
			? questionnaireStatus.some(
					(step) => step.type === 'householdSingleCohabitant' && !step.answered,
				)
			: false

// #endregion

const cannotRunWhenCaseCompleted: FlowModule['canRunModule'] = ({
	flowCompleted,
}) => {
	return !flowCompleted
}

/**
 * Runs through all check and short circuits if any of the checks return false
 * @param args moduleChecks to run through
 */
const andCanRunModule = (
	...args: FlowModule['canRunModule'][]
): FlowModule['canRunModule'] => {
	return async (props) => {
		for (const canRun of args) {
			const result = await canRun(props)
			if (!result) {
				return false
			}
		}

		return true
	}
}

const canAndShouldRunQuestionnaire: FlowModule['canRunModule'] = ({
	caseConfiguration,
	questionnaireStatus,
}) => {
	switch (true) {
		case hasModule(
			caseConfiguration,
			ModuleDefinition.PersonalInformationQuestionnaire,
		):
		case hasModule(
			caseConfiguration,
			ModuleDefinition.PersonalInformationCprOnlyQuestionnaire,
		):
		case hasModule(
			caseConfiguration,
			ModuleDefinition.PersonalInformationExtendedQuestionnaire,
		):
		case hasModule(caseConfiguration, ModuleDefinition.HouseholdQuestionnaire):
		case hasModule(
			caseConfiguration,
			ModuleDefinition.HouseholdSingleCohabitantQuestionnaire,
		):
		case hasModule(caseConfiguration, ModuleDefinition.AssetQuestionnaire): {
			logger.debug('questionnaire modules found', questionnaireStatus)
			return questionnaireStatus.some((step) => !step.answered)
		}
		default:
			logger.debug('No questionnaire modules found')
			return false
	}
}
const canAndShouldRunLandingPage: FlowModule['canRunModule'] = ({
	caseConfiguration,
}) => {
	return (
		hasModule(caseConfiguration, ModuleDefinition.LandingPage) &&
		!userConsentManager.hasGivenConsent('flow')
	)
}

const canAndShouldRunClientConsent: FlowModule['canRunModule'] = ({
	caseConfiguration,
}) => {
	if (caseConfiguration.consents.length > 0) {
		return true
	}

	return false
}

export const calculateModuleForRun = async ({
	caseConfiguration,
	isFinalFlow,
	module,
	questionnaireStatus,
	flowCompleted,
	processingStatuses,
}: {
	module: FlowModule
	isFinalFlow: boolean
	caseConfiguration: SmartCheckConfigurationDto
	questionnaireStatus: QuestionnaireStep[]
	flowCompleted: boolean
	processingStatuses: ProcessingStatusDto[]
}): Promise<null | FlowModule> => {
	if (module.onlyForFinalFlow && !isFinalFlow) {
		return null
	}
	const canRun = await module.canRunModule({
		caseConfiguration,
		questionnaireStatus,
		flowCompleted,
		processingStatuses,
	})
	if (!canRun) {
		return null
	}

	return {
		...module,
	}
}

export interface FlowModule {
	type: ModuleDefinitionType
	// Next transition should use building animation?
	route: string
	/** Defines if a module added for tracking only and doesn't need routing */
	synthetic?: boolean
	canBeCompletedAsync: boolean
	onlyForFinalFlow?: boolean
	/** @deprecated only added for making mixpanel events match for old events for those events that doesn't match module name */
	oldMixpanelEventName?: string | null
	dataSharingType: 'personal' | 'economy' | 'overview' | 'none'
	// module order could be changed to be based on dependencies on other modules
	moduleOrder: number
	// Could also be checked by type if undefined or string if needed
	canRunModule: ({
		caseConfiguration,
	}: {
		caseConfiguration: SmartCheckConfigurationDto
		questionnaireStatus: QuestionnaireStep[]
		flowCompleted: boolean
		processingStatuses: ProcessingStatusDto[]
	}) => boolean | Promise<boolean>
	reopenAction?: (
		history: HistoryWithEntries,
		moduleSettings: { reopenableModules: string[] },
		eventPayload?: Record<string, any>,
	) => void | Promise<void>
}

const canAndShouldRunIncomeVerification = ({
	caseConfiguration,
}: {
	caseConfiguration: SmartCheckConfigurationDto
}): boolean => hasModule(caseConfiguration, ModuleDefinition.IncomeVerification)

const canAndShouldRunEskat = ({
	caseConfiguration,
}: {
	caseConfiguration: SmartCheckConfigurationDto
}): boolean => hasModule(caseConfiguration, ModuleDefinition.Eskat)

const canAndShouldRunPension = ({
	caseConfiguration,
}: {
	caseConfiguration: SmartCheckConfigurationDto
}): boolean => hasModule(caseConfiguration, ModuleDefinition.Pension)

const canAndShouldRunBank = ({
	caseConfiguration,
}: {
	caseConfiguration: SmartCheckConfigurationDto
}): boolean =>
	hasModule(caseConfiguration, ModuleDefinition.Bank) ||
	hasModule(caseConfiguration, ModuleDefinition.AccountVerification)

const canAndShouldRunDebtOverview = ({
	caseConfiguration,
}: {
	caseConfiguration: SmartCheckConfigurationDto
}): boolean => hasModule(caseConfiguration, ModuleDefinition.DebtOverview)

const canAndShouldRunAssetOverview = ({
	caseConfiguration,
}: {
	caseConfiguration: SmartCheckConfigurationDto
}): boolean => hasModule(caseConfiguration, ModuleDefinition.AssetOverview)

const canAndShouldRunAssetExpenses = ({
	caseConfiguration,
}: {
	caseConfiguration: SmartCheckConfigurationDto
}): boolean => hasModule(caseConfiguration, ModuleDefinition.AssetExpenses)

const canAndShouldRunAssetReplacement = ({
	caseConfiguration,
}: {
	caseConfiguration: SmartCheckConfigurationDto
}): boolean => hasModule(caseConfiguration, ModuleDefinition.AssetReplacement)

const canAndShouldRunSavingsCapacity = ({
	caseConfiguration,
}: {
	caseConfiguration: SmartCheckConfigurationDto
}): boolean => hasModule(caseConfiguration, ModuleDefinition.SavingsCapacity)

const canAndShouldRunCustomQuestionnaire = ({
	caseConfiguration,
}: {
	caseConfiguration: SmartCheckConfigurationDto
}): boolean =>
	hasModule(caseConfiguration, ModuleDefinition.CustomQuestionnaire)

const canAndShouldRunBudget = ({
	caseConfiguration,
}: {
	caseConfiguration: SmartCheckConfigurationDto
}): boolean => hasModule(caseConfiguration, ModuleDefinition.Budget)

const canAndShouldRunBudgetConsumerEmail = ({
	caseConfiguration,
}: {
	caseConfiguration: SmartCheckConfigurationDto
}): boolean =>
	hasModule(caseConfiguration, ModuleDefinition.BudgetConsumerEmail)

// If async, check if coapppliants are completed
// - If coapplicant isn't completed start flow with a screen displaying info for starting the other flow
// -- Same screen could be used for waiting screen when last async module is completed
// Maybe add should run module to each module
// Maybe it should be a method on the service to register modules
export const flowModules: FlowModule[] = [
	{
		type: ModuleDefinition.FlowStarted,
		route: '/',
		canBeCompletedAsync: true,
		synthetic: true,
		dataSharingType: 'none',
		moduleOrder: 0,
		canRunModule: cannotRunWhenCaseCompleted,
	},
	// Maybe add flow resumed event
	// Can also be added logic in tracking synthetics
	{
		type: ModuleDefinition.SamsungBrowserWarning,
		route: config.routeCodes.samsungBrowserWarning,
		canBeCompletedAsync: true,
		dataSharingType: 'none',
		moduleOrder: 0.5,
		canRunModule: () => !!navigator.userAgent?.match(/SamsungBrowser/g),
	},
	{
		type: ModuleDefinition.LandingPage,
		route: config.routeCodes.flowSplash,
		canBeCompletedAsync: true,
		dataSharingType: 'none',
		moduleOrder: 1,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunLandingPage,
		),
		oldMixpanelEventName: 'flowSplash',
	},
	{
		type: ModuleDefinition.ClientConsent,
		route: config.routeCodes.clientConsent,
		canBeCompletedAsync: true,
		dataSharingType: 'none',
		moduleOrder: 2,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunClientConsent,
		),
	},
	{
		type: ModuleDefinition.Questionnaire,
		route: config.routeCodes.consumerInputs,
		canBeCompletedAsync: true,
		dataSharingType: 'personal',
		moduleOrder: 10,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunQuestionnaire,
		),
		oldMixpanelEventName: 'consumerInputs',
	},
	// #region Current Synthetic modules
	{
		type: ModuleDefinition.PersonalQuestionnaire,
		route: '/',
		synthetic: true,
		canBeCompletedAsync: true,
		dataSharingType: 'none',
		moduleOrder: 12,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunPersonalQuestionnaire,
		),
	},
	{
		type: ModuleDefinition.HouseholdQuestionnaire,
		route: '/',
		synthetic: true,
		canBeCompletedAsync: true,
		dataSharingType: 'none',
		moduleOrder: 14,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunHouseholdQuestionnaire,
		),
	},
	{
		type: ModuleDefinition.HouseholdSingleCohabitantQuestionnaire,
		route: '/',
		synthetic: true,
		canBeCompletedAsync: true,
		dataSharingType: 'none',
		moduleOrder: 15,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunHouseholdSingleCohabitantQuestionnaire,
		),
	},
	{
		type: ModuleDefinition.AssetQuestionnaire,
		route: '/',
		synthetic: true,
		canBeCompletedAsync: true,
		dataSharingType: 'none',
		moduleOrder: 16,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunAssetQuestionnaire,
		),
	},
	{
		type: ModuleDefinition.Eskat,
		route: config.routeCodes.eskat,
		canBeCompletedAsync: true,
		dataSharingType: 'economy',
		moduleOrder: 20,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunEskat,
		),
	},
	{
		type: ModuleDefinition.Pension,
		route: config.routeCodes.pension,
		canBeCompletedAsync: true,
		dataSharingType: 'economy',
		moduleOrder: 25,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunPension,
		),
	},
	{
		type: ModuleDefinition.Bank,
		route: config.routeCodes.bankSelectionPage,
		canBeCompletedAsync: true,
		dataSharingType: 'economy',
		moduleOrder: 30,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunBank,
		),
		reopenAction: (history, settings) => {
			history.push('banks/reopen-session', {
				reopenableModules: settings.reopenableModules,
			})
		},
		oldMixpanelEventName: 'bankSelectionPage',
	},
	{
		type: ModuleDefinition.AssetOverview,
		route: config.routeCodes.assetOverview,
		canBeCompletedAsync: false,
		onlyForFinalFlow: true,
		dataSharingType: 'overview',
		moduleOrder: 40.1,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunAssetOverview,
		),
	},
	{
		type: ModuleDefinition.AssetExpenses,
		route: config.routeCodes.assetExpenses,
		canBeCompletedAsync: false,
		onlyForFinalFlow: true,
		dataSharingType: 'overview',
		moduleOrder: 40.5,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunAssetExpenses,
		),
	},
	{
		type: ModuleDefinition.AssetReplacement,
		route: config.routeCodes.assetReplacement,
		canBeCompletedAsync: false,
		onlyForFinalFlow: true,
		dataSharingType: 'overview',
		moduleOrder: 40.8,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunAssetReplacement,
		),
	},
	{
		type: ModuleDefinition.DebtOverview,
		route: config.routeCodes.debtOverview,
		canBeCompletedAsync: false,
		onlyForFinalFlow: true,
		dataSharingType: 'overview',
		moduleOrder: 40.3,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunDebtOverview,
		),
	},
	{
		type: ModuleDefinition.SavingsCapacity,
		route: config.routeCodes.savingsCapacity,
		canBeCompletedAsync: false,
		onlyForFinalFlow: true,
		dataSharingType: 'overview',
		moduleOrder: 40.5,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunSavingsCapacity,
		),
	},
	{
		type: ModuleDefinition.CustomQuestionnaire,
		route: config.routeCodes.customQuestionnaire,
		canBeCompletedAsync: false,
		dataSharingType: 'none',
		moduleOrder: 17,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunCustomQuestionnaire,
		),
	},
	{
		type: ModuleDefinition.Budget,
		route: config.routeCodes.miniBudget,
		canBeCompletedAsync: false,
		onlyForFinalFlow: true,
		dataSharingType: 'overview',
		moduleOrder: 41,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunBudget,
		),
		oldMixpanelEventName: 'miniBudget',
	},
	{
		type: ModuleDefinition.IncomeVerification,
		route: config.routeCodes.incomeVerification,
		canBeCompletedAsync: false,
		onlyForFinalFlow: true,
		dataSharingType: 'overview',
		moduleOrder: 41,
		canRunModule: andCanRunModule(
			cannotRunWhenCaseCompleted,
			canAndShouldRunIncomeVerification,
		),
	},
	{
		type: ModuleDefinition.FlowCompletion,
		route: config.routeCodes.budgetCreator,
		canBeCompletedAsync: false,
		dataSharingType: 'none',
		moduleOrder: 100,
		canRunModule: cannotRunWhenCaseCompleted,
		oldMixpanelEventName: null,
	},
	{
		type: ModuleDefinition.BudgetConsumerEmail,
		route: config.routeCodes.budgetConsumerEmail,
		canBeCompletedAsync: false,
		onlyForFinalFlow: true,
		dataSharingType: 'none',
		moduleOrder: 101,
		canRunModule: canAndShouldRunBudgetConsumerEmail,
	},
	{
		type: ModuleDefinition.DocumentUpload,
		route: config.routeCodes.documentUpload,
		canBeCompletedAsync: false,
		dataSharingType: 'none',
		moduleOrder: 102,
		// TODO: Add logic for checking if document upload is needed
		canRunModule: andCanRunModule(
			({ flowCompleted }) => {
				return flowCompleted
			},
			({ processingStatuses }) => {
				return processingStatuses.some(
					(status) => status.status === 'documentationRequested',
				)
			},
		),
	},
	{
		type: ModuleDefinition.Finished,
		route: config.routeCodes.flowCompleted,
		canBeCompletedAsync: false,
		dataSharingType: 'none',
		moduleOrder: 110,
		canRunModule: () => true,
		oldMixpanelEventName: 'flowCompleted',
	},
]

export const deletedModule: FlowModule = {
	type: ModuleDefinition.Deleted,
	route: config.routeCodes.flowDeleted,
	canBeCompletedAsync: false,
	dataSharingType: 'none' as const,
	moduleOrder: 0,
	canRunModule: () => true,
}

export const coApplicantNotCompletedModule: FlowModule = {
	type: ModuleDefinition.CoApplicantNotCompleted,
	route: config.routeCodes.incompleteParringApplicantWarning,
	canBeCompletedAsync: true,
	dataSharingType: 'none' as const,
	moduleOrder: 0,
	canRunModule: () => true,
	oldMixpanelEventName: null,
}

const hasModule = (
	caseConfiguration: SmartCheckConfigurationDto,
	moduleType: ModuleDefinitionType,
) => {
	const modules = caseConfiguration.moduleConfiguration.moduleIds

	return modules.some((module) => module === moduleType)
}

const modulePathMap = new Map<string, string>(
	flowModules.map((module) => [module.route, module.type]),
)

export const findModuleByPath = (path: string) => modulePathMap.get(path)

export const mixpanelEventNameToModuleTypeMap = new Map<string, string>(
	flowModules
		.filter((module) => module.oldMixpanelEventName !== undefined)
		.map((module) => [module.type, module.oldMixpanelEventName]) as [
		string,
		string,
	][],
)

export const modulesWithoutFlowEvents = new Set<string>([
	ModuleDefinition.FlowStarted,
	ModuleDefinition.CoApplicantNotCompleted,
	ModuleDefinition.DocumentUpload,
	ModuleDefinition.FlowCompletion,
	ModuleDefinition.BudgetConsumerEmail,
	ModuleDefinition.Finished,
	ModuleDefinition.Deleted,
	ModuleDefinition.SamsungBrowserWarning,
])

export const modulesInConfiguration = async (
	configuration: AuthenticateSmartcheckSessionDto,
) => {
	const isFinalFlow = configuration.isFinalFlow
	const isReadyToStart = configuration.isReadyToStart

	const modules = isReadyToStart
		? await Promise.all(
				flowModules.map((module) =>
					calculateModuleForRun({
						caseConfiguration: configuration.smartCheckConfiguration,
						module,
						isFinalFlow,
						questionnaireStatus: configuration.questionnaireStatus || [],
						flowCompleted: configuration.finishedOn != null,
						processingStatuses: configuration.case.processingStatuses || [],
					}),
				),
			)
		: [coApplicantNotCompletedModule]

	return modules
		.filter((module): module is FlowModule => module !== null)
		.sort((a, b) => a.moduleOrder - b.moduleOrder)
}
